// Pricing migration 32 (ADO-3985), T13: the multi-buy dialog, mounted once at the composition root
// (`main.tsx`, beside the toast host) so every strip, list row and order review nudge opens the
// same dialog through `useDealDialogStore`.
//
// - Built on the app's Modal (the confirmed frame 06 property), chrome-less so the deal view draws
//   its red header: role="dialog" and aria-modal, named by the deal's title, focus trapped,
//   Escape and ✕ close, and focus returns to the strip that opened it (06; the Multi-buy Dialog
//   note). Done just closes (06).
// - Desktop and tablet: the 600px centred dialog, Touch-sized on a tablet (t/06). A phone: the
//   bottom sheet, closed by ✕, the scrim, Done or a drag down (m/06; the WCAG 2.5.7 alternative).
// - The deal is read live from the deals store, so its progress follows each stepper change;
//   "Also on this product" swaps the deal in place (R4 07o). A deal that leaves the read while
//   open stays as last shown (D-6: the server decides at the next change). A deal asked for that
//   has nothing to show closes at once, so it never pops open when a later answer brings it.

import { useEffect, useRef } from 'react';
import type { MultibuyDeal } from '../api/multibuy-deals.client';
import { useIsNarrowViewport } from '../basket/useIsNarrowViewport';
import { Modal } from '../design-system';
import { PHONE_MEDIA_QUERY, TABLET_DOWN_MEDIA_QUERY } from '../design-system/breakpoints';
import { dealView } from './dealView';
import { MultibuyDealView } from './MultibuyDealView';
import { useDealDialogStore } from './useDealDialogStore';
import { useMultibuyDealsEntry } from './useMultibuyDealsStore';

export function MultibuyDealDialogHost() {
  const target = useDealDialogStore((s) => s.target);
  const close = useDealDialogStore((s) => s.close);
  const showDeal = useDealDialogStore((s) => s.showDeal);
  const entry = useMultibuyDealsEntry(target?.skuCode ?? '');
  const phone = useIsNarrowViewport(PHONE_MEDIA_QUERY);
  const touch = useIsNarrowViewport(TABLET_DOWN_MEDIA_QUERY);
  const lastShown = useRef<MultibuyDeal | null>(null);

  const deals = entry?.state === 'ready' ? entry.deals : [];
  const live = deals.find((deal) => deal.multibuyOfferCode === target?.offerCode) ?? null;
  if (target === null) lastShown.current = null;
  else if (live !== null) lastShown.current = live;
  const deal =
    target !== null && lastShown.current?.multibuyOfferCode === target.offerCode
      ? lastShown.current
      : null;
  const nothingToShow = target !== null && deal === null;
  useEffect(() => {
    if (nothingToShow) close();
  }, [nothingToShow, close]);
  if (target === null || deal === null) return null;

  const title = dealView(deal, { wow: false, lost: false, availableLines: 0 }).header.title;
  return (
    <Modal
      open
      onClose={close}
      title={title}
      hideChrome
      size="medium"
      placement={phone ? 'bottom' : 'center'}
    >
      <MultibuyDealView
        deal={deal}
        layout={phone ? 'sheet' : 'dialog'}
        touch={touch}
        otherDeals={deals.filter((other) => other.multibuyOfferCode !== deal.multibuyOfferCode)}
        onShowDeal={showDeal}
        onDone={close}
        onClose={close}
      />
    </Modal>
  );
}
